import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * Incorporation Post (Post-Incorporation Package) 전용 챗봇 플로우 — M32101.
 *
 * 이미 Sync 된 Entity 를 선택하면 entity 정보 (directors / secretaries / shareholders / 주소)
 * 를 우측 패널이 prefill. 12개 문서 (Phase 1 필수 7 + Phase 2 정관 3 + Phase 3 조건부 2) 를
 * 단일 panel 의 section 토글로 처리.
 *
 * 흐름: Task Title 입력 → Entity 선택 → confirm → (panel 에서 변수 입력 + Create Task)
 */
export const incorporationPostFlow: ChatFlow = {
  id: 'incorporation-post',
  categoryMatch: ['incorporation(post)', 'incorporation (post)', 'post-incorporation', 'post incorporation'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `You selected **${preSet.task.TASK_NAME}** (Post-Incorporation).\n\n` +
          `This preset includes ${docCount} document(s).\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for the post-incorporation package.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (Post-Incorporation).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. Please fill the post-incorporation details on the right panel and click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    // ── Person Lookup steps (panel 의 AI 아이콘 클릭 시 진입) ──
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage: 'Person details loaded. The form has been pre-filled. You can also drag/click the fields below.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    // ── Address chooser ──
    {
      id: 'address-list',
      aiMessage: 'This person has multiple addresses. Pick the one to use.',
      widget: 'address-options',
      awaitEvent: true,
    },
    // ── Direct address input — panel 의 주소 필드 ✨ 클릭 시 진입 ──
    {
      id: 'address-input',
      aiMessage: 'Please enter the address. It will be filled back to the form automatically.',
      widget: 'address-input',
      awaitEvent: true,
    },
    // ── Co-Sec Firm contact lookup (Co-Sec Firm Name 의 AI 아이콘 클릭 시 진입) ──
    {
      id: 'contact-list',
      aiMessage: 'Select the Co-Sec firm from the contact list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          { role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [{
            role: 'ai',
            content: 'Please select an entity first.',
            timestamp: Date.now(), stepId: 1, source: 'system',
          }],
        };
      }
      // Co-Sec Firm lookup → contact list (not officer list)
      if (ctx.fieldGroup === 'cosecFirm') {
        return {
          messages: [{
            role: 'user',
            content: 'Looking up Co-Sec firm from contacts...',
            timestamp: Date.now(), stepId: 1,
          }],
          nextStepId: 'contact-list',
          nextStepWidgetData: { entityCd, selectedRole: 'CONTACT' },
        };
      }
      if (ctx.defaultRole) {
        return {
          messages: [{
            role: 'user',
            content: `Looking up **${ctx.fieldGroup}** (role: ${ctx.defaultRole})...`,
            timestamp: Date.now(), stepId: 1,
          }],
          nextStepId: 'officer-list',
          nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
        };
      }
      return {
        messages: [{
          role: 'user',
          content: `Looking up **${ctx.fieldGroup}**...`,
          timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'officer-role',
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{
          role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [{
          role: 'user',
          content: `Selected person: **${officerName}**`,
          timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    // Co-Sec Firm contact selected → show officer-detail (field picker) like other person lookups
    if (eventName === 'officer-select' && context.currentStepId === 'contact-list') {
      const contact = payload || {};
      const firmName = contact.contactName || contact.name || 'Selected contact';
      return {
        messages: [{
          role: 'user',
          content: `Selected Co-Sec firm: **${firmName}**`,
          timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'contact-list') {
      return { nextStepId: 'confirm' };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      // directInput: true → chooser 대신 address-input 위젯 (직접 입력) 으로 진입.
      if (ctx.directInput) {
        return {
          messages: [{
            role: 'user',
            content: `Enter the address for **${ctx.subjectLabel || ctx.fieldGroup || 'the form'}**.`,
            timestamp: Date.now(), stepId: 1,
          }],
          nextStepId: 'address-input',
        };
      }
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      return {
        messages: [{
          role: 'user',
          content: `Choose an address for **${ctx.subjectLabel || 'this person'}**.`,
          timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    // address-input 위젯 submit (ChatMalaysiaAddressInput.submit → ConversationPanel: entity-field-select).
    //   직접 입력된 주소를 selectedAddress 로 흘리도록 chatflow 가 명시적으로 처리.
    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      const value = typeof payload === 'string' ? payload : payload?.value || payload?.address || '';
      if (!value) return {};
      return {
        messages: [{
          role: 'user',
          content: `Entered: **${value}**`,
          timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [{
          role: 'user',
          content: `Selected: **${opt.label || opt.type}**`,
          timestamp: Date.now(), stepId: 1,
        }],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
